import React from 'react'
import ReactDOM from 'react-dom'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import { applyMiddleware, createStore } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import createSagaMiddleware from 'redux-saga'
import rootSaga from './dux/actions'

import { configureAmplify, setStore } from './dux/modules/utils'
import { ingestHandoff } from './dux/modules/ssoHandoff'
import { initRum, recordRumError } from './dux/modules/rum'
import { installInputSanitizer } from './lib/inputSanitizer'
import indexReducer from './dux/reducers'

import 'font-awesome/css/font-awesome.min.css'
import './index.css'
import App from './components/App'
import coBrandInfo from './config/co-brand.config'
if (!window.crypto) window.crypto = window.msCrypto

// Strip invisible characters and trim whitespace on every text input before
// any validator sees the value (see lib/inputSanitizer.js).
installInputSanitizer()

//** REMOVE sensitive data from redux dev tools **//
const sanitizeState = (state) => {
  let sanitizedState = state
  // INVITATIONS
  if (state.invitations) {
    sanitizedState = {
      ...sanitizedState,
      invitations: undefined,
    }
  }

  // USER INFORMATION
  if (state.permissions && state.permissions.users) {
    sanitizedState = {
      ...sanitizedState,
      permissions: {
        ...sanitizedState.permissions,
        users: undefined,
        deletedUsers: undefined,
      },
    }
  }

  return sanitizedState
}
// ** END Remove ** //

const sagaMiddleware = createSagaMiddleware()
const composeEnhancers =
  process.env.NODE_ENV === 'development'
    ? composeWithDevTools({ stateSanitizer: sanitizeState, trace: true })
    : (f) => f

let store = createStore(
  indexReducer,
  composeEnhancers(applyMiddleware(sagaMiddleware)),
)
sagaMiddleware.run(rootSaga)
setStore(store)

export default store

// e@optionlab.com, 2024-10-15: patched these in; updating `react-redux` would be better, but
// it could have too many unintended consequences, so this will bridge the gap
export const useDispatch = () => store.dispatch
export const useSelector = (selector) => selector(store.getState())

const root = document.getElementById('root')

function renderApp(config) {
  const mask = config.mask
  if (mask && coBrandInfo[mask]) {
    const { tabTitle, favicon, className } = coBrandInfo[mask]
    root.className = className || mask.split('.')[0]
    const icon = document.getElementById('tabicon')
    if (favicon) {
      icon.href = favicon
    }
    if (tabTitle) {
      document.title = tabTitle
    }
  }

  ReactDOM.render(
    <Provider store={store}>
      <BrowserRouter>
        <App config={config} />
      </BrowserRouter>
    </Provider>,
    root,
  )
}

if (process.env.NODE_ENV === 'development') {
  console.log('Running in development mode; using local config')
  const config = require('./config').default
  initRum(config)
  try {
    configureAmplify(config)
    renderApp(config)
  } catch (err) {
    recordRumError(err)
    throw err
  }
  window.store = store
} else {
  fetch('/config.json')
    .then((res) => {
      res
        .json()
        .then(async (config) => {
          // override domain / bucket / etc. for custom domain mask
          // admin.bam.zone is an administrative alias for the same tenant — no mask needed
          const adminAliases = ['admin.bam.zone']
          if (
            config.domain !== window.location.hostname &&
            !adminAliases.includes(window.location.hostname)
          ) {
            config.mask = window.location.hostname
          }

          // rebrand redirect
          if (window.location.hostname === 'paccarwinch.bam.zone') {
            window.location = 'https://arrowheadwinch.bam.zone'
          } else if (window.location.hostname === 'kta.bam.zone') {
            window.location = 'https://viably.bam.zone'
          }

          initRum(config)
          try {
            // Adopt a cross-domain login handoff (#bam_sso) BEFORE Amplify and
            // the app read the auth session, so the DAM starts already signed in.
            await ingestHandoff(config)
            configureAmplify(config)
            renderApp(config)
          } catch (err) {
            recordRumError(err)
            throw err
          }
        })
        .catch((err) => console.log(err))
    })
    .catch((err) => console.log(err))
}
